import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { poolAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Trophy, Search, Users, DollarSign, Calendar, Lock, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

function HighlightText({ text, query }) {
  if (!query || !text) return <>{text}</>;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return <>{parts.map((part, i) => regex.test(part) ? <mark key={`h-${i}-${part}`} className="bg-[#f3f0ff] text-[#5b3cc4] font-semibold px-0.5 rounded">{part}</mark> : part)}</>;
}

export default function BrowsePoolsPage() {
  const [pools, setPools] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => { document.title = "Find a Pool — Pools FC"; }, []);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await poolAPI.list({ search: search || undefined });
        setPools(res.data.pools);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [search]);

  const handlePoolClick = (pool) => {
    if (pool.status === 'locked') {
      toast.error('This pool is locked and no longer accepting new entries.');
      return;
    }
    if (user) {
      navigate(pool.is_member ? `/pools/${pool.id}` : `/pools/${pool.id}/join`);
    } else {
      localStorage.setItem('pfc_intent', 'join_pool');
      localStorage.setItem('pfc_selected_pool', pool.id);
      navigate('/register');
    }
  };

  const statusColors = { open: 'bg-green-100 text-green-800', locked: 'bg-yellow-100 text-yellow-800' };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-white/95 backdrop-blur">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 flex h-[64px] sm:h-[120px] items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading font-bold text-lg">
            <img src="/poolsfc-logo.png" alt="Pools FC" className="h-[48px] w-[48px] sm:h-[104px] sm:w-[104px] rounded-lg" />
            Pools FC
          </Link>
          <div className="flex items-center gap-3">
            {!user && <Link to="/login" className="text-sm font-medium text-muted-foreground hover:text-foreground">Sign In</Link>}
            {!user && <Button asChild size="sm" className="bg-[#5b3cc4] hover:bg-[#4e33a8]"><Link to="/register">Create Account</Link></Button>}
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 mb-6">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" />Back</Link>
        </div>

        <h1 className="font-heading text-3xl font-bold mb-2">Find Your Pool</h1>
        <p className="text-muted-foreground mb-6">Search by pool name or organizer. {!user && 'Create an account to join.'}</p>

        <div className="relative max-w-md mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search pools by name or organizer..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-40 rounded-2xl bg-muted animate-pulse" />)}
          </div>
        ) : pools.length === 0 ? (
          <div className="text-center py-16">
            <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-lg font-semibold mb-1">No Pools Found</h3>
            <p className="text-muted-foreground text-sm">{search ? 'Try a different search.' : 'No pools available yet.'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pools.map(pool => (
              <div key={pool.id} onClick={() => handlePoolClick(pool)} className="cursor-pointer">
                <Card className={`transition-shadow h-full ${pool.status === 'locked' ? 'opacity-60' : 'hover:shadow-md cursor-pointer'}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="font-heading text-lg"><HighlightText text={pool.name} query={search} /></CardTitle>
                      <Badge className={statusColors[pool.status] || 'bg-gray-100'}>{pool.status}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{pool.entry_count || 0} / {pool.plan_capacity || 8} entries</span>
                      <Badge className={pool.plan && pool.plan !== 'free' ? 'bg-[#f3f0ff] text-[#5b3cc4] text-[10px]' : 'text-[10px]'} variant={!pool.plan || pool.plan === 'free' ? 'secondary' : 'default'}>{pool.plan_label || 'Free'}</Badge>
                      {pool.entry_deadline && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{new Date(pool.entry_deadline).toLocaleDateString()}</span>}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-muted-foreground">by <HighlightText text={pool.organizer_name} query={search} /></span>
                      {pool.status === 'locked' ? (
                        <span className="text-xs text-yellow-600 font-medium flex items-center gap-1"><Lock className="h-3 w-3" />Pool Locked</span>
                      ) : !user ? (
                        <span className="text-xs text-[#5b3cc4] font-medium flex items-center gap-1"><Lock className="h-3 w-3" />Create account to join</span>
                      ) : pool.is_member ? (
                        <Badge className="bg-[#f3f0ff] text-[#5b3cc4] text-[10px]">Member</Badge>
                      ) : (
                        <span className="text-xs text-[#5b3cc4] font-medium">Join →</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
